import React, {
  useId,
  useMemo,
  useRef,
  useState,
} from "react"
import { useDispatch, useSelector } from "react-redux"
import { createCustomName, customNames as customNamesSelector } from "reduxSlices/customNamesSlice"
import { useModal } from "hooks"
import { DEFAULT_NAMES, KEYS } from "utils/customNameHelpers"
import { squish } from "utils/stringHelpers"
import FieldLabel from "shared/FieldLabel"
import Modal, { ModalHeader, DEFAULT_MODAL_STYLE } from "shared/Modal"
import { CustomIconButton, PrimaryButton, TertiaryButton } from "shared/buttons"
import { TextInput } from "shared/inputs"
import { Select } from "shared/selects"
import CustomNamePreview from "./CustomNamePreview"

const FORM_ID = "create-custom-name-form"

const AddCustomName = () => {
  const dispatch = useDispatch()
  const { modalIsOpen, openModal, closeModal } = useModal()
  const formRef = useRef()
  const selectContainerRef = useRef()
  const replacesLabelId = useId()

  const openSelect = () => {
    selectContainerRef.current?.querySelector(".react-dropdown-select")?.click()
  }

  const customNames = useSelector(customNamesSelector)

  const [key, setKey] = useState(null)
  const [rawName, setRawName] = useState("")

  const name = squish(rawName)

  // A key may only be named once, so the keys already named are not offered.
  // Memoized so that the options keep their identity between renders.
  const keyOptions = useMemo(() => {
    const namedKeys = customNames.map((customName) => customName.key)

    return KEYS
      .filter((renameableKey) => !namedKeys.includes(renameableKey))
      .map((renameableKey) => ({ label: DEFAULT_NAMES[renameableKey], value: renameableKey }))
  }, [customNames])

  const updateRawName = (value) => {
    setRawName(value.trimStart())
  }

  const reset = () => {
    setKey(null)
    setRawName("")
    formRef.current?.reset()
  }

  const cancel = () => {
    reset()
    closeModal()
  }

  const create = (event) => {
    event.preventDefault()

    if (!key || !name) return

    dispatch(createCustomName({
      key,
      name,
      onSuccess: () => {
        reset()
        closeModal()
      },
    }))
  }

  if (keyOptions.length === 0) return null

  return (
    <>
      <CustomIconButton
        className="self-start tertiary-button bg-white"
        icon="add"
        onClick={openModal}
        text="Add Custom Name"
      />
      <Modal isOpen={modalIsOpen} style={DEFAULT_MODAL_STYLE}>
        <div className="flex flex-col">
          <ModalHeader closeModal={cancel} heading="New Custom Name" />
          <form className="flex flex-col gap-8 w-full" id={FORM_ID} onSubmit={create} ref={formRef}>
            {/*
              The select is deliberately not wrapped in a FieldLabel: a label forwards a
              click to the control it labels, which reopens the dropdown the moment an
              option is chosen.  The heading is tied to the select through aria-labelledby
              instead, which also replaces the generic label the dropdown gives itself.
            */}
            <div ref={selectContainerRef}>
              {/*
                The dropdown opens on a click of its own root, which is what the heading
                forwards.  Keyboard users reach the dropdown directly -- it is focusable
                and opens on its own -- so the handler is a pointer convenience only.
              */}
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
              <span className="block mb-1 w-max cursor-pointer" id={replacesLabelId} onClick={openSelect}>
                Replaces
              </span>
              <Select
                additionalProps={{ "aria-labelledby": replacesLabelId }}
                isFullWidth
                onChange={setKey}
                options={keyOptions}
                value={key}
              />
            </div>
            <FieldLabel className="w-full !max-w-full" text="Custom Name">
              <TextInput className="bordered-base-input w-full" onChange={updateRawName} value={rawName} />
            </FieldLabel>
            {name && <CustomNamePreview name={name} />}
            <div className="flex items-center justify-center gap-2">
              <TertiaryButton ariaLabel="cancel" onClick={cancel} text="Cancel" />
              <PrimaryButton
                ariaLabel="create custom name"
                disabled={!key || !name}
                form={FORM_ID}
                text="Submit"
                type="submit"
              />
            </div>
          </form>
        </div>
      </Modal>
    </>
  )
}

export default AddCustomName
